'use client'

import { CookieConsentConfig } from 'vanilla-cookieconsent'
import { WEBSITE_LINKS } from '../lib/constants'

/**
 * All config. options available here:
 * https://cookieconsent.orestbida.com/reference/configuration-reference.html
 */
export const getCookieConsentConfig = (
  domain: string
): CookieConsentConfig => ({
  root: '#__next',
  mode: 'opt-in',
  autoShow: true,
  revision: 0,
  manageScriptTags: true,
  autoClearCookies: true,
  hideFromBots: true,
  disablePageInteraction: false,
  lazyHtmlGeneration: true,

  guiOptions: {
    consentModal: {
      layout: 'cloud inline',
      position: 'bottom center',
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      autoClear: {
        cookies: [
          {
            name: /^_ga/, // regex: match all cookies starting with '_ga'
          },
          {
            name: '_gid', // string: exact cookie name
          },
        ],
      },

      // https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
      services: {
        googleAnalytics: {
          label: 'Google Analytics',
          cookies: [
            {
              name: /^(_ga|_gid)/,
            },
            {
              name: /^_gat/,
            },
            {
              name: /^AMP_TOKEN/,
            },
            {
              name: /^gac/,
            },
          ],
        },
      },
    },
  },

  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'We use cookies',
          description:
            'We use cookies to improve your experience. By clicking "Accept all", you agree to the use of cookies. You can change your cookie settings at any time by clicking "Manage Individual preferences".',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          showPreferencesBtn: 'Manage Individual preferences',
          footer: `
                      <a href=${WEBSITE_LINKS.privacyPolicy} target="_blank">Privacy Policy</a>
                  `,
        },
        preferencesModal: {
          title: 'Manage cookie preferences',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          savePreferencesBtn: 'Accept current selection',
          closeIconLabel: 'Close modal',
          serviceCounterLabel: 'Service|Services',
          sections: [
            {
              title: 'Your Privacy Choices',
              description: `In this panel you can express some preferences related to the processing of your personal information. You may review and change expressed choices at any time by resurfacing this panel via the provided link. To deny your consent to the specific processing activities described below, switch the toggles to off or use the “Reject all” button and confirm you want to save your choices.`,
            },
            {
              title: 'Strictly Necessary',
              description:
                'These cookies are essential for the proper functioning of the website and cannot be disabled.',

              cookieTable: {
                caption: 'Cookie table',
                headers: {
                  name: 'Cookie',
                  domain: 'Domain',
                  desc: 'Description',
                },
                body: [
                  {
                    name: 'NEXT_LOCALE',
                    domain,
                    desc: "The locale of the user, used to display the website in the user's language",
                  },
                  {
                    name: process.env.NEXT_PUBLIC_AUTH_COOKIE_NAME || '',
                    domain,
                    desc: 'The auth cookie, used to authenticate the user, and store basic user information',
                  },
                  {
                    name: 'cc_cookie',
                    domain,
                    desc: "The cookie consent cookie, used to store the user's cookie consent preferences",
                  },
                  // HelpScout cookies
                  {
                    name: 'persist:hs-beacon-*',
                    domain,
                    desc: 'HelpScout cookie, to store if a message has been shown in the chatbot.',
                  },
                  {
                    name: 'persist:hs-beacon-message-*',
                    domain,
                    desc: 'HelpScout cookie, to store if a message has been shown in the chatbot.',
                  },
                  {
                    name: 'HS:Beacon:Suggestions:*',
                    domain,
                    desc: 'HelpScout cookie, used by the chatbot.',
                  },
                  {
                    name: 'HS:Beacon:Suggestions:id:*',
                    domain,
                    desc: 'HelpScout cookie, used by the chatbot.',
                  },
                ],
              },
              linkedCategory: 'necessary',
            },
            {
              title: 'Performance and Analytics',
              description:
                'These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.',
              linkedCategory: 'analytics',
              cookieTable: {
                caption: 'Cookie table',
                headers: {
                  name: 'Cookie',
                  domain: 'Domain',
                  desc: 'Description',
                },
                body: [
                  // Google Analytics cookies
                  {
                    name: '_ga',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: '_gid',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: '_gat',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: 'ga<container-id>',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: 'AMP_TOKEN',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: 'gac<property-id>',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: 'ga<container-id>',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                  {
                    name: 'ga<container-id>',
                    domain,
                    desc: 'Google Analytics cookie, used to track user behavior on the website',
                  },
                ],
              },
            },
            {
              title: 'More information',
              description: `For any queries in relation to my policy on cookies and your choices, please send an email to <a href=${WEBSITE_LINKS.help}>support@mydra.io</a>`,
            },
          ],
        },
      },
    },
  },
})
